import { useEffect, useState } from 'react'
import { servidorAlcancavel } from './api.js'

/// Estado de conexão **com o servidor**, não com o roteador.
///
/// `navigator.onLine` responde apenas "existe uma interface de rede ativa". Numa
/// obra isso é quase sempre verdade e quase sempre inútil: o Wi-Fi do canteiro
/// conecta num roteador sem link, o 4G oscila entre uma barra e nenhuma, e o
/// aparelho continua se dizendo online. A única resposta honesta é ter perguntado
/// ao servidor há pouco tempo.

export type EstadoConexao = 'verificando' | 'online' | 'offline'

/// Com sinal, confere de dois em dois minutos: o suficiente para o card não mentir
/// por muito tempo, sem torrar bateria nem franquia de dados.
const INTERVALO_OK = 120_000
/// Sem sinal, insiste mais: o inspetor quer saber assim que voltar.
const INTERVALO_FALHA = 20_000

export function useConexao(): { estado: EstadoConexao; verificadoEm: Date | null; verificar: () => void } {
  const [estado, setEstado] = useState<EstadoConexao>('verificando')
  const [verificadoEm, setVerificadoEm] = useState<Date | null>(null)
  const [gatilho, setGatilho] = useState(0)

  useEffect(() => {
    let vivo = true
    let timer: ReturnType<typeof setTimeout> | undefined

    async function conferir() {
      if (!vivo) return
      // Sem interface de rede não vale gastar um pedido: é offline com certeza.
      const alcancou = navigator.onLine ? await servidorAlcancavel() : false
      if (!vivo) return
      setEstado(alcancou ? 'online' : 'offline')
      setVerificadoEm(new Date())
      timer = setTimeout(conferir, alcancou ? INTERVALO_OK : INTERVALO_FALHA)
    }

    void conferir()

    // Os eventos do navegador não são a verdade, mas são um bom motivo para
    // perguntar de novo na hora em vez de esperar o próximo ciclo.
    const reconferir = () => {
      clearTimeout(timer)
      void conferir()
    }
    window.addEventListener('online', reconferir)
    window.addEventListener('offline', reconferir)
    document.addEventListener('visibilitychange', reconferir)

    return () => {
      vivo = false
      clearTimeout(timer)
      window.removeEventListener('online', reconferir)
      window.removeEventListener('offline', reconferir)
      document.removeEventListener('visibilitychange', reconferir)
    }
  }, [gatilho])

  return { estado, verificadoEm, verificar: () => setGatilho((n) => n + 1) }
}
